import { defineEvent, withPii } from "@amtana/module";
import { z } from "zod";

// Auth's facts (ADR-022 point 1, S26.02): one stream per user
// (`auth-user:<userId>`), so a sign-up, a verification request, a
// password-reset request, an email-change request and an attempt on a taken
// address each leave a trace nothing has to call for. A fact carries ids and
// what a person may see, never a token, a link, a password or a hash
// (ADR-022 point 7): the link itself is minted only once a delivery sends it
// (S26.12). Nothing reacts to these yet (S26.05 is the router); auth still
// mails through the outbox beside every append (D440).

export const AUTH_USER = "auth-user";

export const userSignedUp = withPii(
  defineEvent(AUTH_USER, "user_signed_up", z.object({ userId: z.string(), email: z.string() })),
  ["email"],
);

/** A verification link was (re)minted: from sign-up itself, or a later resend. */
export const emailVerificationRequested = withPii(
  defineEvent(AUTH_USER, "email_verification_requested", z.object({ userId: z.string(), email: z.string() })),
  ["email"],
);

export const passwordResetRequested = withPii(
  defineEvent(AUTH_USER, "password_reset_requested", z.object({ userId: z.string(), email: z.string() })),
  ["email"],
);

export const emailChangeRequested = withPii(
  defineEvent(
    AUTH_USER,
    "email_change_requested",
    z.object({ userId: z.string(), oldEmail: z.string(), newEmail: z.string() }),
  ),
  ["oldEmail", "newEmail"],
);

/** From sign-up (someone else's address) or from a requested email change (the new address
 * is already someone else's); `userId`/`email` name the address's owner, never whoever asked.
 * `requestedByEmail` is the exception, set only from an email change (sign-up has no address of
 * its own to notify): the requester's own current address, so the router can send it the same
 * link-free notice an email change already states when it goes through (D508). */
export const takenAddressAttempted = withPii(
  defineEvent(
    AUTH_USER,
    "taken_address_attempted",
    z.object({ userId: z.string(), email: z.string(), requestedByEmail: z.string().optional() }),
  ),
  ["email", "requestedByEmail"],
);

export const authUserEvents = [
  userSignedUp,
  emailVerificationRequested,
  passwordResetRequested,
  emailChangeRequested,
  takenAddressAttempted,
] as const;
